<template>
  <div>
    <site-header />
    <div class="main-grid">
        <div class="px-6 py-2 md:p-6 bg-gray-100">
          <header class="mb-3">
            <h2 @click="editingDescription = !editingDescription"><strong>{{list ? list.name : 'Ideas'}}</strong></h2>
            <p v-if="list && !editingDescription" @click="editingDescription = !editingDescription" class="color-gray-light">{{list.description}}</p>
            <input v-if="editingDescription" ref="description" @keyup.enter="updateDescription" @blur="updateDescription" v-model="list.description" class="input-description w-full text-sm mb-2">
            <div v-if="canClaimList" class="my-2">
              <div v-if="sentClaimEmail">
                <p class="text-xs text-green"><strong>Sent!</strong> Check your email to claim this list.</p>
              </div>
              <div v-else>
                <button v-if="!isClaiming" @click.prevent="isClaiming = true" class="bg-blue-400 hover:bg-blue-800 text-white font-bold py-1 px-2 text-sm rounded">
                  Claim this list
                </button>
                <div v-else>
                  <form @submit.prevent="sendClaimEmail">
                    <input v-model="claimEmail" required name="email" type="email" placeholder="Email" class="p-2 block w-full border border-gray bg-white">
                    <button type="submit" class="block w-full bg-blue-400 hover:bg-blue-800 font-bold text-sm text-white p-2">
                      Claim via email
                    </button>
                    <button class="block text-center bg-gray-100 w-full p-2 text-xs text-gray my-2" @click.prevent="isClaiming = false">
                      Cancel
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <div v-if="list.ideas.length">
              <button @click.prevent="toggleView" class="bg-green-400 hover:bg-green-800 text-white font-bold py-1 px-2 text-sm rounded">
                View {{view === 'list' ? 'on Map' : 'List'}}
              </button>
              
            </div>

          </header>

            <div v-if="view === 'list'">
              <div class="idea-list">
                <idea-card :data-id="idea.id" v-for="idea in list.ideas" :key="idea.id" :idea="idea" :removable="true" @remove="removeIdea(idea.id)"></idea-card>
              </div>

              <div class="mb-3">
                <textarea ref="pasteInput" style="min-height: 135px" v-model="textToParse" class="block w-full border border-gray bg-white p-2" placeholder="Paste in places..."></textarea>
              </div>

              <help-box list-page />              

              <div v-if="textToParse">
                <button 
                  :disabled="listIsLoading" 
                  class="p-2 text-xs text-white bg-indigo-700 mb-2"
                  :style="listIsLoading ? 'opacity:0.7' : ''"
                  @click="lookupPlaces">
                  {{listIsLoading ? 'Looking up' : 'Lookup' }} these places{{listIsLoading ? '...' : '' }}
                </button>
      
                <div id="place-results" class="visible"></div>
              </div>

              <div v-if="newIdeas">
                <div v-for="(newIdea, counter) in newIdeas" :key="newIdea.id" class="mb-1">
                  <idea-card v-if="counter >= loadedIdeas" :idea="newIdea" />
                </div>
              </div>

              <div class="mt-5">
                <button 
                  class="block text-left bg-gray-100 w-full p-1 text-xs text-gray"
                  @click.prevent="$router.push('/create')">
                  ← Create a new list
                </button>
              </div>
            </div>
            <div v-else>
              <map-view />
            </div>
          
        </div>
    </div>
  </div>
</template>

<script>
import SiteHeader from '@/components/SiteHeader'
import HelpBox from '@/components/HelpBox'
import IdeaCard from '@/components/IdeaCard'
import MapView from '@/components/MapView'
import draggable from 'vuedraggable'
import { mapActions, mapGetters } from 'vuex'
import axios from 'axios'
import mongoose from 'mongoose'

async function asyncForEach(array, callback) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
}

export default {
  components: {
    HelpBox,
    SiteHeader,
    IdeaCard,
    MapView,
    draggable
  },

  mounted () {
    this.getList({slug: this.$route.params.slug}).then(() => {
      if (!this.list.ideas.length) {
        this.$refs.pasteInput.focus()
      }
    })
  },

  data () {
    return {
      toolboxOpen: false,
      isClaiming: false,
      claimEmail: null,
      sentClaimEmail: false,
      textToParse: null,
      newIdeas: [],
      loadedIdeas: 0,
      listIsLoading: false,
      openHelp: false,
      editingDescription: false,
      view: 'list',
    }
  },
  computed: {
    ...mapGetters([
      'list'
    ]),
    canClaimList () {
      // #TODO - for now turn off Claiming of lists
      return false // this.list && !this.list.owner && !this.list.claim_email
    }
  },
  methods: {
    ...mapActions([
      'addIdeaToList',
      'getList',
      'removeIdeaFromList',
      'submitClaimEmail',
      'updateIdeaOnList',
      'updateIdeaSilently',
      'updateList',
    ]),
    changeDay (addDays) {
      const newIndex = this.currentDayIndex + addDays
      if (newIndex < 0) {
        this.currentDayIndex = 0
      } else if (newIndex >= this.days.length) {
        this.currentDayIndex = this.days.length - 1
      } else {
        this.currentDayIndex = newIndex
      }
    },
    removeIdea (ideaId) {
      this.removeIdeaFromList({list: this.list, ideaId})
    },
    sendClaimEmail () {
      this.sentClaimEmail = true
      this.submitClaimEmail({list: this.list, email: this.claimEmail})
    },
    parseLine (line) {
      line = line.trim()
      if (!line || line === '') {
        return null
      }
      const possibleCommentDelimeters = ['//', '--', '::', '##']
      let pieces = []
      let name = null
      let comment = null
      let i = 0

      // Search this string to see if one we found one of the delimeters
      while (!comment && i < possibleCommentDelimeters.length) {
        pieces = line.split(possibleCommentDelimeters[i])
        name = pieces.shift().trim()
        if (pieces.length > 0) {
          comment = pieces.join().trim()
        }
        i++
      }

      return {name, comment}
    },
    createIdeaFromText (index, listLine) {
      return {
        _id: index,
        name: listLine.name,
        fromApi: false,
        description: listLine.comment,
        image: null
      }
    },
    clearList () {
      this.newIdeas = []
      this.listIsLoading = false
    },
    renderList () {
      this.clearList()
      // Break up into array of lines
      let parsed = null

      if (!this.textToParse) {
        return
      }
      const lines = this.textToParse.split('\n')
      lines.forEach((line, index) => {
        parsed = this.parseLine(line)
        if (!parsed) {
          return
        }
        this.newIdeas.push(this.createIdeaFromText(index, parsed))
      })
    },
    lookupPlaces () {
      if (!this.newIdeas) {
        return
      }

      let placeId = null
      let place = null
      let newIdea = null
      let updatedList = null
      
      this.listIsLoading = true
      this.loadedIdeas = 0

      const startTime = Date.now()
      let time = -2000

      this.newIdeas.forEach(async (idea, index) => {
        this.listIsLoading = true
        await setTimeout(async () => {
          this.listIsLoading = true
          placeId = await this.lookupPlaceId(idea)
          place = await this.getPlaceDetails(placeId)
          newIdea = this.replaceWithApiVersion(place, index)
          updatedList = await this.addIdeaToList({idea: newIdea, list:this.list})
          this.replacePhotos(newIdea)
          this.listIsLoading = false
          this.loadedIdeas++
        }, time += 2000)
      })
    },
    lookupPlaceId (idea) {
      // Search the Google API for the place
      return new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            const request = {
              query: `${idea.name} ${this.list.name}`,
              fields: ['name', 'place_id']
            }
            const placesService = new google.maps.places.PlacesService(document.getElementById('place-results'))
            placesService.findPlaceFromQuery(request, function (results, status) {
              if (status === google.maps.places.PlacesServiceStatus.OK && results.length) {
                resolve(results[0].place_id)
              } else {
                reject(status)
              }
            })
          } catch (e) {
            return reject(e);
          }
        }, 500);
      })
    },
    getPlaceDetails (placeId) {
      // return promise
      const fields = ['name', 'url', 'website', 'photos', 'vicinity', 'price_level', 'geometry', 'formatted_address']
      return new Promise(function(resolve, reject) {
        setTimeout(async () => {
          try {
            // use getDetails method to retrieve Place data via the Place's place_id property
            const placesService = new google.maps.places.PlacesService(document.getElementById('place-results'))
            placesService.getDetails({placeId, fields}, function(place, status) {
              if (status == google.maps.places.PlacesServiceStatus.OK) {
                // upon successful request resolve place
                resolve(place)
              } else {
                // else reject with status
                reject(status)
              }
            })
          } catch (e) {
            return reject(e);
          }
        }, 500);
      })
    },
    replaceWithApiVersion (place, index) {
      let idea = {
        id: mongoose.Types.ObjectId().toString(),
        place_id: place.placeId,
        name: place.name,
        favorite: true,
        slot: null,
        day: null,
        icon: '⭐️',
        image: place.photos && place.photos.length ? place.photos[0].getUrl({'maxWidth': 200, 'maxHeight': 200}) : null,
        images: [],
        url: place.url,
        address: place.vicinity,
        website: place.website,
        description: this.newIdeas[index].description,
        location: {
          type: 'Point',
          coordinates: [place.geometry.location.lng(), place.geometry.location.lat()],
          address: place.formatted_address
        }
      }

      if (place.photos && place.photos.length) {
        place.photos.forEach((photo) => {
          idea.images.push(photo.getUrl({'maxWidth': 400, 'maxHeight': 400}))
        })
      }
      
      return idea
    },
    async replacePhotos(idea) {
      asyncForEach(idea.images, async (url, index) => {
        await this.uploadToCloudinary(url, index, idea)
      })
    },
    async uploadToCloudinary(oldUrl, index, idea) {
      if (oldUrl.includes('cloudinary')) {
        return
      }
      const cloudinary = {
         uploadPreset: 'bestspots',
         apiKey: '247778674891827',
         cloudName: 'thatericsmith',
      }
      const url = `https://api.cloudinary.com/v1_1/${cloudinary.cloudName}/upload`

      const formData = new FormData()
      formData.append('file', oldUrl)
      formData.append('upload_preset', cloudinary.uploadPreset)
      formData.append('tags', 'bestspots')
      await axios.post(url, formData)
        .then(async res => {
          await this.updateImageOnIdea(idea, index, res.data.secure_url)
        })
        .catch(async () => {
          await this.updateImageOnIdea(idea, index, null)
        })
    },
    async updateImageOnIdea(idea, index, url) {
      console.log(idea.name, index, url)
      idea.images[index] = url
      await this.updateIdeaSilently({
          data: {
            images: idea.images,
          },
          listId: this.list._id,
          ideaId: idea.id
        })
    },
    toggleView () {
      this.view = this.view === 'list' ? 'map' : 'list'
    },
    updateDescription () {
      this.updateList({_id: this.list._id, description: this.list.description})
      this.editingDescription = false
    },
  },
  watch: {
    textToParse () {
      this.renderList()
    },
    loadedIdeas (newLoaded) {
      if (newLoaded > 0 && newLoaded == this.newIdeas.length) {
        this.textToParse = ''
      }
    }
  }
}
</script>

<style>
h2 {
  font-size: 22px;
}
</style>
<style scoped>
.main-grid {
  height:100vh;
}


.idea-header {
  display: flex;
  justify-content: space-between;
  align-items:center;
}

.idea-list > * {
  margin: 8px 0;
}
.idea-list-small > * {
  margin: 5px 0 0;
}

.plan-header {
  display:grid;
  grid-template-columns: 30px 1fr 30px;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// ListPage.vue?cf964a6a